// file: src/pages/pengamatan/persen-bunga/PersenBungaDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Slide,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Download as DownloadIcon, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material';
import FormPersenBunga from './FormPersenBunga';
import FormPersenBunga02 from './FormPersenBunga02';
import { 
  generatePersenBungaPDFFromDOM, 
  generateHighQualityPDFAlternative
} from '../../../utils/pdfGenerator';
import { generatePersenBungaExcel } from '../../../utils/excelGenerator';

// Slide component untuk transition
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

{/*
*/}

const PersenBungaDialog = ({ open, onClose, groupData, loading, error }) => {
  const [expanded, setExpanded] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDownloadClick = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

const handleDownloadPDF = async () => {
  handleDownloadMenuClose();
  setPdfGenerating(true);

  try {
    console.log('Starting PDF dengan rowspan fix...');
    showSnackbar('Memperbaiki format tabel...', 'info');
    
    // Phase 1: Apply PDF optimization
    const containers = document.querySelectorAll('.container');
    containers.forEach(container => {
      container.classList.add('pdf-optimized');
    });

    // Phase 2: Manual table header fix sebelum capture
    containers.forEach(container => {
      const tables = container.querySelectorAll('.table-form');
      tables.forEach(table => {
        // Force semua cells visible secara manual
        const allCells = table.querySelectorAll('td, th');
        allCells.forEach(cell => {
          // Khusus untuk cells dengan rowspan
          if (cell.hasAttribute('rowspan')) {
            cell.style.cssText = `
              visibility: visible !important;
              display: table-cell !important;
              opacity: 1 !important;
              background: #f8f9fa !important;
              border: 2px solid #000 !important;
              font-weight: bold !important;
              position: relative !important;
              z-index: 100 !important;
              font-size: 9px !important;
            `;
          }
          
          // Force border visibility
          cell.style.border = '1px solid #000';
          cell.style.visibility = 'visible';
          cell.style.opacity = '1';
        });
      });
    });

    // Phase 3: Wait for fixes to apply
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Phase 4: Scroll and ensure visibility
    containers.forEach(container => {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Phase 5: Generate PDF
    showSnackbar('Membuat PDF...', 'info');
    await generatePersenBungaPDFFromDOM(groupData);
    
    showSnackbar('PDF berhasil didownload!', 'success');
    
  } catch (error) {
    console.error('Error dengan rowspan fix:', error);
    showSnackbar('Gagal mendownload PDF. Silakan coba lagi.', 'error');
  } finally {
    setPdfGenerating(false);
    
    // Cleanup
    setTimeout(() => {
      const containers = document.querySelectorAll('.container');
      containers.forEach(container => {
        container.classList.remove('pdf-optimized');
        // Reset manual styles
        const tables = container.querySelectorAll('.table-form');
        tables.forEach(table => {
          const cells = table.querySelectorAll('td, th');
          cells.forEach(cell => {
            cell.style.cssText = '';
          });
        });
      });
    }, 2000);
  }
};

const handleDownloadExcel = async () => {
  handleDownloadMenuClose();
  
  try {
    showSnackbar('Membuat file Excel...', 'info');
    
    await generatePersenBungaExcel(groupData);
    
    showSnackbar('Excel berhasil didownload!', 'success');
  } catch (error) {
    console.error('Error downloading Excel:', error);
    showSnackbar('Gagal mendownload Excel. Silakan coba lagi.', 'error');
  }
};

  // Hitung total plot dari semua items
  const totalPlot = groupData?.items?.reduce((total, item) => total + (item.plotCount || 0), 0) || 0;

  // Fungsi untuk mendapatkan info plot dalam format string dengan koma
  const getInfoPlot = (item) => {
    if (!item.originalData || !item.originalData.plot) return "-";
    
    const plotInfo = [];
    Object.values(item.originalData.plot).forEach(plot => {
      if (plot.plot && plot.luas_plot && plot.pjp) {
        plotInfo.push(`${plot.plot}`);
      }
    });
    
    return plotInfo.length > 0 ? plotInfo.join(", ") : "-";
  };

  // Reset state ketika dialog dibuka
  useEffect(() => {
    if (open) {
      setPdfGenerating(false);
      setSnackbar({ open: false, message: '', severity: 'info' });
    }
  }, [open]);

  if (!groupData) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Detail Pengamatan Lokasi - {groupData.lokasi}
          </Typography>
          
          {/* Download Menu */}
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleDownloadClick}
            title="Download Data"
            disabled={pdfGenerating}
          >
            {pdfGenerating ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
          </IconButton>
          
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={handleDownloadMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
          >
            <MenuItem onClick={handleDownloadPDF} disabled={pdfGenerating}>
              <ListItemIcon>
                <PictureAsPdf fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {pdfGenerating ? 'Generating PDF...' : 'Download PDF'}
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDownloadExcel}>
              <ListItemIcon>
                <TableChart fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download Excel</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Tabel Informasi Lokasi */}
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell colSpan={5}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpanded(!expanded)}
                      sx={{ mr: 1 }}
                    >
                      {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <Typography variant="h6" component="div">
                      Informasi Lokasi
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: 'primary.50' }}>
                <TableCell><strong>ID Lokasi</strong></TableCell>
                <TableCell><strong>Lokasi</strong></TableCell>
                <TableCell><strong>Mandor</strong></TableCell>
                <TableCell><strong>Jumlah Data</strong></TableCell>
                <TableCell><strong>Total Plot</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {groupData.id_lokasi}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {groupData.lokasi}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    {groupData.nama_mandor}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${groupData.items.length} Data`} 
                    color="primary" 
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${totalPlot} Plot`} 
                    color="secondary" 
                    variant="outlined" 
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Collapsible Table Container */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <TableContainer component={Paper}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell><strong>Id Pengamatan</strong></TableCell>
                    <TableCell><strong>Tanggal Pengamatan</strong></TableCell>
                    <TableCell><strong>Pengamat</strong></TableCell>
                    <TableCell><strong>Jumlah Plot</strong></TableCell>
                    <TableCell><strong>Info Plot</strong></TableCell>
                    <TableCell><strong>Status Plot</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupData.items.map((item, index) => (
                    <TableRow key={`${item.id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.id_pengamatan}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.tanggal_pengamatan}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.nama_pengamat}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${item.plotCount} Plot`} 
                          size="small" 
                          color="secondary" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {getInfoPlot(item)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label="Approved" 
                          size="small" 
                          color="success" 
                          variant="filled" 
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Collapse>

        {/* Form Persen Bunga */}
        <FormPersenBunga groupData={groupData} />

        <FormPersenBunga02 groupData={groupData} />
      </Box>

      {/* Snackbar untuk notifikasi */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Dialog>
  );
};

export default PersenBungaDialog;